import {
    NOTICE_GET_ALL,
    NOTICE_SET_FILTERS,
    NOTICE_CLEAR_FILTERS,
    NOTICE_SET_STATE,
    NOTICE_PAGE_RESET,
    NOTICE_SET_LOADING,
    NOTICE_SET_ERROR,
    NOTICE_CREATE,
    NOTICE_CREATE_SUCCESS,
    NOTICE_CREATE_FAILURE
} from "./NoticeActionType";

// Types
export interface NoticeItem {
    id: number;
    category: string;
    priority: string;
    year: string;
    title: string;
    content: string;
    author: string;
    commentCount: number;
    readCount: number;
    totalCount: number;
    readPercentage: number;
    createdDate?: any;
    isActive?: boolean;
    success?: any;
    message?: any;
}

export interface NoticeFilterRequest {
    includeInactive?: boolean;
    category?: string;
    priority?: string;
    year?: string;
}

export interface CreateNoticeRequest {
    title: string;
    content: string;
    category: string;
    priority: string;
    year: string;
    author: string;
}

export interface ApiResponseDTO {
    isSuccess: boolean;
    message: string;
    accessedOn: any;
    records: NoticeItem[];
}

export interface NoticeState {
    notices: NoticeItem[];
    filters: NoticeFilterRequest;
    loading: boolean;
    error: string | null;
    lastResponse: any;
    createSuccess: boolean;
    createError: string | null;
    successMessage: string | null;
}

const initialState: NoticeState = {
    notices: [],
    filters: {
        includeInactive: false,
        category: undefined,
        priority: undefined,
        year: undefined
    },
    loading: false,
    error: null,
    lastResponse: null,
    createSuccess: false,
    createError: null,
    successMessage: null
};

const NoticeReducer = (state = initialState, action: any) => {
    console.log("========== NOTICE REDUCER ==========");
    console.log("Action Type:", action.type);
    console.log("Action Payload:", action.payload);
    console.log("Current State:", state);

    switch (action.type) {
        case NOTICE_GET_ALL:
            console.log("Processing NOTICE_GET_ALL");
            const apiResponse = action.payload;

            // Log the full response
            console.log("API Response received:", apiResponse);
            console.log("API Response type:", typeof apiResponse);
            console.log("Is apiResponse an object?", apiResponse && typeof apiResponse === 'object');

            if (apiResponse) {
                console.log("API Response keys:", Object.keys(apiResponse));
                console.log("isSuccess:", apiResponse.isSuccess);
                console.log("records:", apiResponse.records);
                console.log("records is array?", Array.isArray(apiResponse.records));
            }

            // Extract notices from the response
            let noticesList: NoticeItem[] = [];

            if (apiResponse && apiResponse.isSuccess === true && apiResponse.records && Array.isArray(apiResponse.records)) {
                noticesList = apiResponse.records;
                console.log("✅ Extracted notices from records:", noticesList.length);
            } else if (apiResponse && apiResponse.records && Array.isArray(apiResponse.records)) {
                noticesList = apiResponse.records;
                console.log("✅ Extracted notices from records (without isSuccess check):", noticesList.length);
            } else if (Array.isArray(apiResponse)) {
                noticesList = apiResponse;
                console.log("✅ Response is directly an array:", noticesList.length);
            } else if (apiResponse && apiResponse.data && Array.isArray(apiResponse.data)) {
                noticesList = apiResponse.data;
                console.log("✅ Extracted notices from data:", noticesList.length);
            } else {
                console.log("❌ Could not extract notices from response");
                if (apiResponse) {
                    console.log("Response structure:", JSON.stringify(apiResponse, null, 2));
                }
            }

            // Log the extracted notices
            console.log("Final notices list length:", noticesList.length);
            if (noticesList.length > 0) {
                console.log("First notice sample:", noticesList[0]);
                console.log("All years in notices:", noticesList.map(n => n.year));
            }

            // Get error message if any
            const errorMessage = apiResponse?.isSuccess === false
                ? apiResponse.message || "Failed to fetch notices"
                : null;

            const newState = {
                ...state,
                notices: noticesList,
                lastResponse: apiResponse,
                loading: false,
                error: errorMessage,
                // Reset create-related flags when loading new notices
                createSuccess: false,
                createError: null,
                successMessage: null
            };

            console.log("New state after NOTICE_GET_ALL:", newState);
            return newState;

        case NOTICE_SET_FILTERS:
            console.log("Setting filters in reducer:", action.payload);
            const filtersState = {
                ...state,
                filters: {
                    ...state.filters,
                    ...action.payload
                }
            };
            console.log("New filters state:", filtersState);
            return filtersState;

        case NOTICE_CLEAR_FILTERS:
            console.log("Clearing filters in reducer");
            const clearState = {
                ...state,
                filters: {
                    includeInactive: false,
                    category: undefined,
                    priority: undefined,
                    year: undefined
                }
            };
            console.log("State after clearing filters:", clearState);
            return clearState;

        case NOTICE_SET_STATE:
            console.log("Setting state in reducer:", action.payload);
            const setState = {
                ...state,
                ...action.payload,
                loading: false
            };
            console.log("State after SET_STATE:", setState);
            return setState;

        case NOTICE_SET_LOADING:
            console.log("Setting loading in reducer:", action.payload);
            return {
                ...state,
                loading: action.payload,
                error: action.payload ? null : state.error
            };

        case NOTICE_SET_ERROR:
            console.log("Setting error in reducer:", action.payload);
            return {
                ...state,
                loading: false,
                error: action.payload?.message || "An error occurred",
                lastResponse: action.payload
            };

        case NOTICE_PAGE_RESET:
            console.log("Resetting notice page to initialState");
            return initialState;

        // Handle notice creation
        case NOTICE_CREATE:
            console.log("Processing NOTICE_CREATE:", action.payload);
            return {
                ...state,
                loading: true,
                createSuccess: false,
                createError: null,
                successMessage: null,
                error: null
            };

        case NOTICE_CREATE_SUCCESS:
            console.log("Notice created successfully:", action.payload);
            
            // Extract the new notice from response if available
            let newNotice = null;
            if (action.payload && action.payload.records && Array.isArray(action.payload.records) && action.payload.records.length > 0) {
                newNotice = action.payload.records[0];
            }

            return {
                ...state,
                loading: false,
                createSuccess: true,
                createError: null,
                successMessage: action.payload?.message || "Notice created successfully!",
                error: null,
                lastResponse: action.payload,
                // Optionally add the new notice to the list immediately
                // notices: newNotice ? [newNotice, ...state.notices] : state.notices
            };

        case NOTICE_CREATE_FAILURE:
            console.log("Notice creation failed:", action.payload);
            return {
                ...state,
                loading: false,
                createSuccess: false,
                createError: action.payload,
                successMessage: null,
                error: action.payload,
                lastResponse: action.payload
            };

        default:
            console.log("Unknown action type:", action.type);
            return state;
    }
};

export default NoticeReducer;